import { Controller } from 'stimulus';

export default class extends Controller {
  static targets = [
    'output',
    'loading',
    'taskStatusLabel',
    'taskStatusDetails',
    'taskProgress',
    'completedDetails',
    'inProgressDetails',
  ];

  connect() {
    this.fetchTaskStatus();
    this.interval = setInterval(() => this.fetchTaskStatus(), 3000);
  }

  async fetchTaskStatus() {
    const { taskStatusUrl, completedLabel, inProgressLabel } = this.element.dataset;
    if (!taskStatusUrl) {
      console.error('Task status URL is not provided.');
      return;
    }

    try {
      const response = await fetch(taskStatusUrl, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch task status: ${response.statusText}`);
      }
      const data = await response.json();
      // we want to later reuse data on status, success_count, failed_count, total_count
      Object.keys(data).forEach((key) => {
        const camelizeKey = key.replace(/_([a-z])/g, (_, char) => char.toUpperCase());
        this.element.dataset[camelizeKey] = data[key];
      });

      // data contains: status (scheduled, running, done, or failed), success_count, failed_count, total_count
      if (data.status === 'done' || data.status === 'failed') {
        this.updateTaskProgress(data);
        if (this.hasTaskStatusLabelTarget) {
          this.taskStatusLabelTarget.textContent = completedLabel; // completed or failed
        }
        if (this.hasCompletedDetailsTarget) {
          this.inProgressDetailsTarget.style.display = 'none'; // show completed details
          this.completedDetailsTarget.style.display = ''; // show completed details
        }
        clearInterval(this.interval); // stop fetching data
      } else {
        if (this.hasTaskStatusDetailsTarget) {
          this.taskStatusDetailsTarget.style.display = ''; // show task status details
        }
        if (this.hasTaskStatusLabelTarget) {
          this.taskStatusLabelTarget.textContent = inProgressLabel; // scheduled or running
        }
        this.updateTaskProgress(data);
        if (this.hasInProgressDetailsTarget) {
          this.inProgressDetailsTarget.style.display = ''; // show in progress details
        }
      }
      if (data.status != 'scheduled'
          && (parseFloat(this.element.dataset.successCount) !== 0 || parseFloat(this.element.dataset.failedCount) !== 0)) {
        this.fetchResults();
      }
    } catch (error) {
      console.error('Error fetching task status:', error);
    }
  }

  updateTaskProgress(data) {
    const progressPercentage = data.total_count === 0
      ? 0
      : (
        (100 * (parseFloat(data.success_count) + parseFloat(data.failed_count)))
        / parseFloat(data.total_count)
      ).toFixed(2);

    if (this.hasTaskProgressTarget) {
      this.taskProgressTarget.style.width = `${progressPercentage}%`;
    }
  }

  async fetchResults() {
    const csrfToken = document
      .querySelector('meta[name="csrf-token"]')
      .getAttribute('content');

    try {
      const response = await fetch(this.element.dataset.url, {
        method: 'GET',
        headers: {
          Accept: 'text/vnd.turbo-stream.html, text/html, application/xhtml+xml',
          'X-CSRF-Token': csrfToken, // Include CSRF token
        },
      });

      if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.statusText}`);
      }

      const html = await response.text();
      if (this.hasOutputTarget && parseFloat(this.element.dataset.totalCount) > 0) {
        this.outputTarget.innerHTML = html;
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      if (this.hasOutputTarget) {
        this.outputTarget.insertAdjacentHTML(
          'beforeend',
          '<p>Error loading content.</p>',
        );
      }
    }
  }
}
